/**
 * Admin interface for character moderation
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../../WebApp';

const ADMIN_TOKEN = process.env.REACT_APP_ADMIN_TOKEN || '';

function AdminModeration() {
  const { language } = React.useContext(AuthContext);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [token, setToken] = useState(ADMIN_TOKEN);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editingSystemPrompt, setEditingSystemPrompt] = useState(null);
  const [systemPromptValue, setSystemPromptValue] = useState('');

  const loadCharacters = async () => {
    if (!token) {
      setError(language === 'en' ? 'Admin token required' : 'Требуется токен администратора');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get('/web/characters/admin/pending_moderation', {
        params: { token }
      });
      setCharacters(response.data.characters || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'en' ? 'Failed to load characters' : 'Не удалось загрузить персонажей'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadCharacters();
    } else {
      setLoading(false);
    }
  }, [token]);

  const handleApprove = async (characterId, systemPrompt) => {
    try {
      const payload = systemPrompt ? { system_prompt: systemPrompt } : null;
      await axios.post(`/web/characters/admin/${characterId}/approve`, payload, {
        params: { token }
      });
      setEditingSystemPrompt(null);
      setSystemPromptValue('');
      await loadCharacters();
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'en' ? 'Failed to approve' : 'Не удалось одобрить'));
    }
  };

  const handleReject = async (characterId) => {
    if (!rejectReason.trim()) {
      alert(language === 'en' ? 'Please provide a rejection reason' : 'Пожалуйста, укажите причину отказа');
      return;
    }
    
    try {
      await axios.post(`/web/characters/admin/${characterId}/reject`, 
        { reason: rejectReason },
        { params: { token } }
      );
      setRejectingId(null);
      setRejectReason('');
      await loadCharacters();
    } catch (err) {
      setError(err.response?.data?.detail || (language === 'en' ? 'Failed to reject' : 'Не удалось отклонить'));
    }
  };

  const translations = {
    en: {
      title: 'Character Moderation',
      refresh: 'Refresh',
      loading: 'Loading...',
      noCharacters: 'No characters pending moderation',
      created: 'Created:',
      description: 'Description:',
      appearance: 'Appearance:',
      scenario: 'Scenario:',
      greeting: 'Greeting:',
      generationPrompt: 'Individual Characteristics:',
      systemPrompt: 'System Prompt:',
      approve: 'Approve',
      reject: 'Reject',
      rejectReason: 'Rejection Reason:',
      rejectReasonPlaceholder: 'Enter reason for rejection (will be sent to user)',
      cancel: 'Cancel',
      submitReject: 'Submit Rejection',
      editSystemPrompt: 'Edit',
      approveWithPrompt: 'Approve with Edited Prompt'
    },
    ru: {
      title: 'Модерация персонажей',
      refresh: 'Обновить',
      loading: 'Загрузка...',
      noCharacters: 'Нет персонажей на модерации',
      created: 'Создан:',
      description: 'Описание:',
      appearance: 'Внешность:',
      scenario: 'Сценарий:',
      greeting: 'Приветствие:',
      generationPrompt: 'Индивидуальные характеристики:',
      systemPrompt: 'Системный промпт:',
      approve: 'Одобрить',
      reject: 'Отклонить',
      rejectReason: 'Причина отказа:',
      rejectReasonPlaceholder: 'Введите причину отказа (будет отправлено пользователю)',
      cancel: 'Отмена',
      submitReject: 'Отправить отказ',
      editSystemPrompt: 'Редактировать',
      approveWithPrompt: 'Одобрить с отредактированным промптом',
      editSystemPrompt: 'Редактировать',
      approveWithPrompt: 'Одобрить с отредактированным промптом'
    }
  };

  const t = translations[language] || translations.en;

  if (!token) {
    return (
      <div className="max-w-4xl mx-auto py-10 px-4">
        <div className="bg-gray-800 rounded-lg p-6">
          <h1 className="text-2xl font-bold text-white mb-4">
            {language === 'en' ? 'Admin Moderation' : 'Модерация администратора'}
          </h1>
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-300">
              {language === 'en' ? 'Admin Token' : 'Токен администратора'}
            </label>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-md"
              placeholder={language === 'en' ? 'Enter admin token' : 'Введите токен администратора'}
            />
            <button
              onClick={loadCharacters}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              {language === 'en' ? 'Load' : 'Загрузить'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-10 px-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-white">{t.title}</h1>
        <button
          onClick={loadCharacters}
          className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
        >
          {t.refresh}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-900 text-red-200 rounded">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-white text-center py-12">{t.loading}</div>
      ) : characters.length === 0 ? (
        <div className="text-white text-center py-12">{t.noCharacters}</div>
      ) : (
        <div className="space-y-4">
          {characters.map((char) => (
            <div key={char.id} className="bg-gray-800 rounded-lg p-6">
              <div className="flex gap-6">
                {char.avatar_url && (
                  <img
                    src={char.avatar_url.startsWith('http') ? char.avatar_url : `https://hotbump.online${char.avatar_url}`}
                    alt={char.name}
                    className="w-32 h-32 object-cover rounded-lg flex-shrink-0"
                  />
                )}
                <div className="flex-1 space-y-4">
                  <div>
                    <h2 className="text-xl font-bold text-white mb-2">{char.name}</h2>
                    <p className="text-sm text-gray-400">
                      {t.created} {char.created_at ? new Date(char.created_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>

                  {/* Description */}
                  {char.personality && (char.personality.description || char.personality.public_description) && (
                    <div>
                      <p className="text-sm font-semibold text-gray-300 mb-1">{t.description}</p>
                      <p className="text-sm text-gray-400">
                        {char.personality.description || char.personality.public_description}
                      </p>
                    </div>
                  )}

                  {/* Appearance */}
                  {char.appearance && (
                    <div>
                      <p className="text-sm font-semibold text-gray-300 mb-1">{t.appearance}</p>
                      <div className="text-sm text-gray-400 space-y-1">
                        {Object.entries(char.appearance).map(([key, value]) => (
                          <div key={key}>
                            <span className="capitalize">{key.replace('_', ' ')}:</span> {String(value)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Scenario */}
                  {char.personality && char.personality.scenario && (
                    <div>
                      <p className="text-sm font-semibold text-gray-300 mb-1">{t.scenario}</p>
                      <p className="text-sm text-gray-400">{char.personality.scenario}</p>
                    </div>
                  )}

                  {/* Greeting */}
                  {char.greeting && (
                    <div>
                      <p className="text-sm font-semibold text-gray-300 mb-1">{t.greeting}</p>
                      <p className="text-sm text-gray-400">{char.greeting}</p>
                    </div>
                  )}

                  {/* Generation Prompt */}
                  {char.generation_prompt && (
                    <div>
                      <p className="text-sm font-semibold text-gray-300 mb-1">{t.generationPrompt}</p>
                      <p className="text-sm text-gray-400">{char.generation_prompt}</p>
                    </div>
                  )}

                  {/* System Prompt */}
                  {editingSystemPrompt === char.id ? (
                    <div className="mt-4 p-4 bg-gray-700 rounded-lg">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t.systemPrompt}
                      </label>
                      <textarea
                        value={systemPromptValue}
                        onChange={(e) => setSystemPromptValue(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-600 text-white rounded-md mb-3 font-mono text-xs"
                        rows={10}
                      />
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleApprove(char.id, systemPromptValue)}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                        >
                          {t.approveWithPrompt}
                        </button>
                        <button
                          onClick={() => {
                            setEditingSystemPrompt(null);
                            setSystemPromptValue('');
                          }}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg"
                        >
                          {t.cancel}
                        </button>
                      </div>
                    </div>
                  ) : (
                    char.system_prompt && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-semibold text-gray-300">{t.systemPrompt}</p>
                          <button
                            onClick={() => {
                              setEditingSystemPrompt(char.id);
                              setSystemPromptValue(char.system_prompt);
                            }}
                            className="text-xs text-blue-400 hover:text-blue-300"
                          >
                            {t.editSystemPrompt}
                          </button>
                        </div>
                        <p className="text-sm text-gray-400 whitespace-pre-wrap font-mono">{char.system_prompt}</p>
                      </div>
                    )
                  )}

                  {/* Rejection reason input */}
                  {rejectingId === char.id ? (
                    <div className="mt-4 p-4 bg-gray-700 rounded-lg">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {t.rejectReason}
                      </label>
                      <textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-600 text-white rounded-md mb-3"
                        placeholder={t.rejectReasonPlaceholder}
                        rows={3}
                      />
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleReject(char.id)}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                        >
                          {t.submitReject}
                        </button>
                        <button
                          onClick={() => {
                            setRejectingId(null);
                            setRejectReason('');
                          }}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg"
                        >
                          {t.cancel}
                        </button>
                      </div>
                    </div>
                  ) : editingSystemPrompt !== char.id && (
                    <div className="mt-4 flex gap-3">
                      <button
                        onClick={() => handleApprove(char.id, null)}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                      >
                        {t.approve}
                      </button>
                      <button
                        onClick={() => {
                          setRejectingId(char.id);
                          setRejectReason('');
                        }}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                      >
                        {t.reject}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminModeration;
